// Single source of truth for all product content (overview + deep pages).
import {
    BarChart3,
    Workflow,
    Users,
    Receipt,
    FileSignature,
    Briefcase,
    LineChart,
    Activity,
    PieChart,
    Boxes,
    Hammer,
    ClipboardCheck,
    ClipboardList,
    Target,
    Calendar,
    UserPlus,
    BadgeCheck,
    Award,
    Landmark,
    Banknote,
    Calculator,
    Shield,
    Stamp,
    History,
    GitBranch,
    PackageCheck,
    Wallet,
    ShoppingCart,
    Sparkles,
} from "lucide-react";

export const PRODUCTS = [
    {
        slug: "mativex",
        metaDescription: "Mativex gives operators live revenue, margin, and working-capital views — drillable from group to SKU. No data team required.",
        metaKeywords: "real-time BI, business intelligence, retail analytics, CEO dashboard, KPI monitoring, Mativex, Scitus",
        name: "Mativex",
        sub: "Real-time business intelligence",
        icon: BarChart3,
        tagline: "Every operating number, live — without the spreadsheet stack.",
        description:
            "Mativex is a real-time BI layer for operators. It ingests from your POS, ERP, accounting, payment gateway, and warehouse — and gives you live revenue, margin, working-capital, and cohort views you can drill from group all the way down to a single SKU. No data team required, no dashboards-that-die-after-the-pilot.",
        pillars: [
            {
                icon: LineChart,
                h: "Revenue, refreshed every minute",
                p: "Live revenue, orders, AOV, gross margin, and category mix across every store, channel, and entity.",
            },
            {
                icon: Activity,
                h: "Working-capital signals",
                p: "Inventory ageing, stockout risk, collections heat, and DSO trend — surfaced before they hit the P&L.",
            },
            {
                icon: PieChart,
                h: "Ask in plain English",
                p: "An AI copilot that returns a chart, a comparison, or a one-line answer — grounded only in your data.",
            },
        ],
        features: {
            "Live operating views": [
                "Daily / weekly / monthly P&L with prior-period overlay",
                "Drill-down tree: group → entity → region → store → SKU",
                "Role-based saved views (CEO, CFO, Regional GM, Store Manager)",
                "Threshold alerts: revenue dip, margin slip, stockout, DSO breach — to Slack, Teams, or WhatsApp",
                "Anomaly detection on every KPI tile — flagged in-line",
            ],
            "Decisions in flow": [
                "Inventory ageing buckets with auto-reorder suggestions",
                "Collections heatmap ranked by ageing × probability × ticket size",
                "Customer cohort, repeat-rate, LTV, and acquisition-source ROI",
                "Promotion ROI: pre / during / post in a single chart",
                "Custom metrics in SQL or drag-and-drop formula builder",
            ],
            "Share, schedule, govern": [
                "Public dashboards by link or login (with watermarking)",
                "Scheduled snapshots to email, Slack, or WhatsApp",
                "@-mentions and threaded comments on any chart",
                "Row-level security: a Regional GM only sees their region",
                "One-click export to CSV / PDF / PNG / XLSX",
            ],
        },
        howItWorks: [
            { h: "Connect", p: "Plug into your POS, ERP, accounting, and payment stack via prebuilt connectors — or our REST API for the long-tail." },
            { h: "Map once", p: "Mativex auto-detects products, categories, entities, and locations. You confirm; we lock the schema." },
            { h: "Operate", p: "Run your Monday review, your monthly close, and your board pack off the same live source — no exports, no copy-paste." },
        ],
        useCases: [
            { ind: "Retail", h: "Trade days, not weeks", p: "Every store's day-1 numbers — by category, manager, customer cohort, and basket size — visible by 9:30 AM the next morning." },
            { ind: "Manufacturing", h: "Daily margin truth", p: "Plant output, yield, scrap, and unit-economics drillable from finished SKU back to BOM and machine shift." },
            { ind: "Services", h: "Pipeline meets delivery", p: "Forecast accuracy, booked-vs-billed conversion, and team utilisation in one drill-down — no separate CRM dashboard." },
        ],
        faqs: [
            { q: "How does Mativex connect to my existing systems?", a: "Out-of-the-box connectors for the major POS, accounting, ERP, and payment platforms. For custom systems we offer a REST API and a 24-hour custom-connector turnaround." },
            { q: "How fresh is the data?", a: "Most sources sync every minute. Daily summary tables refresh overnight in your local timezone. Streaming sources (POS, payment gateway) appear in dashboards within ~60 seconds of the transaction." },
            { q: "Can non-technical users build dashboards?", a: "Yes. The dashboard builder is no-code, with drag-to-arrange and a natural-language query bar powered by our AI copilot. Power users still get SQL." },
            { q: "Is my data ever shared?", a: "Never. Each customer gets a logically isolated tenant. SOC 2 controls in progress; encryption in transit and at rest are on by default." },
            { q: "What about row-level access?", a: "Native. Define rules once (\"Regional GM sees only their region\") and Mativex enforces them across every dashboard, scheduled email, and copilot answer." },
        ],
        tier: "Starts on Starter",
        industries: ["Retail", "Real Estate", "Healthcare", "Manufacturing", "Services"],
        launchUrl: "https://mativex.scitus.ai/login",
        launchLabel: "Launch Mativex",
        logo: { src: "/products/mativex-logo.png", fit: "mark", needsLightTile: true },
        heroShot: "/products/shots/mativex-1.png",
        gallery: ["/products/shots/mativex-2.png", "/products/shots/mativex-3.png", "/products/shots/mativex-4.png", "/products/shots/mativex-5.png", "/products/shots/mativex-6.png", "/products/shots/mativex-7.png"],
    },
    {
        slug: "vexen",
        metaDescription: "Vexen is the operations command centre for project and plant businesses. Live OEE, SLA, TAT, and milestone signals in one drill-down.",
        metaKeywords: "operations management, project management software, OEE tracking, SLA management, real estate software, Vexen, Scitus",
        name: "Vexen",
        sub: "Live operations command centre",
        icon: Workflow,
        tagline: "Schedule, OEE, SLA — every operating signal, one drill-down away.",
        description:
            "Vexen is the operations command centre for project, plant, and service-delivery businesses. It unifies schedule, milestones, machine performance, and SLA/TAT signals from your existing ERP and field tools — and gives every layer of leadership a drill-down view that ends in action, not export. For project cost, procurement, and vendor control, pair Vexen with Acrium.",
        pillars: [
            {
                icon: Target,
                h: "Project-level truth",
                p: "Every job carries live schedule, milestones, resource state, and risk — visible from portfolio down to a single task.",
            },
            {
                icon: Activity,
                h: "Plant & service signals",
                p: "OEE, yield, scrap, SLA, TAT, downtime causes — by machine, by line, by service desk, refreshed in seconds.",
            },
            {
                icon: Hammer,
                h: "Execution discipline",
                p: "Auto-escalation when milestones, SLAs, or TATs breach thresholds — to the right owner on Slack, Teams, or WhatsApp.",
            },
        ],
        features: {
            "Visibility at every level": [
                "Portfolio → programme → project → task drill-down with status, owner, and ETA on each node",
                "Live Gantt with critical-path highlighting and slip indicators",
                "Resource allocation across people, equipment, plants, and crews",
                "Risk register with severity × likelihood matrix and mitigation owners",
                "Custom dashboards per persona: COO, Plant Head, Project Director, Service Manager",
            ],
            "Plant & service signals": [
                "Live OEE (availability × performance × quality) by machine, line, and shift",
                "Yield, scrap, downtime, and changeover analytics with Pareto root-cause views",
                "SLA / TAT tracking per service line, team, and ticket category",
                "Threshold-based auto-escalation paths with traceable acknowledgement",
                "Daily and weekly operations rhythms scheduled to leadership inboxes",
            ],
            "Built for the field": [
                "Mobile-first capture for site supervisors, plant operators, and service techs",
                "Photo-stamped, geo-tagged progress updates with offline buffering",
                "Offline mode that syncs cleanly on reconnect — including approval queues",
                "WhatsApp / SMS / push notifications to crew leads and shift in-charges",
                "Voice-to-text site notes for low-friction reporting",
            ],
        },
        howItWorks: [
            { h: "Model", p: "Configure your project, plant, or service taxonomy — entities, sites, lines, and SLAs." },
            { h: "Capture", p: "Field, plant, and service teams update progress, risks, and downtime from mobile — online or off." },
            { h: "Steer", p: "Leadership runs operations from a live, drill-down dashboard with auto-escalations baked in." },
        ],
        useCases: [
            { ind: "Real Estate", h: "Bookings to handover", p: "Tower-wise schedule, milestone slippage flags, and absorption against booked inventory — board-pack ready by Monday morning." },
            { ind: "Manufacturing", h: "Plant command centre", p: "Live OEE, yield, scrap, and downtime causes by machine and shift — with root-cause Pareto in one click." },
            { ind: "Healthcare", h: "Network operations", p: "OPD, OT, and bed-occupancy SLAs per provider and unit — with TAT auto-escalation to the floor manager." },
        ],
        faqs: [
            { q: "Does Vexen replace my ERP?", a: "No. Vexen sits above your ERP and unifies operational signals — schedule, OEE, SLA, TAT — across plants, projects, and service lines. We integrate with the major ERPs (SAP, Oracle, Microsoft, Tally, Zoho) and homegrown systems via REST API." },
            { q: "How is Vexen different from Acrium?", a: "Vexen owns operational execution — schedule, milestones, OEE, SLA, TAT. Acrium owns the commercial layer — project cost, procurement, inventory, and vendor control. Most operators run both together; the data shares one customer/project master." },
            { q: "How long is implementation?", a: "Typical go-live for a single project type or plant is 4–6 weeks, including data migration and training. Multi-entity rollouts run 8–12 weeks." },
            { q: "Can field teams use it offline?", a: "Yes. The capture app works fully offline with a local queue and syncs on reconnect — approvals included." },
            { q: "Which devices does it support?", a: "Modern web (Chrome/Edge/Safari/Firefox) and native mobile (iOS 15+, Android 9+). Plant kiosks are supported via dedicated lightweight mode." },
        ],
        tier: "Growth and above",
        industries: ["Real Estate", "Manufacturing", "Healthcare", "Services"],
        launchUrl: "https://vexen.scitus.ai/login",
        launchLabel: "Open Vexen",
        logo: { src: "/products/vexen-logo-light.png", fit: "lockup" },
        heroShot: "/products/shots/vexen-1.png",
        gallery: ["/products/shots/vexen-2.png", "/products/shots/vexen-3.png", "/products/shots/vexen-4.png", "/products/shots/vexen-5.png", "/products/shots/vexen-6.png"],
    },
    {
        slug: "karya",
        metaDescription: "Karya handles attendance, payroll, and the full employee lifecycle — geo, face, biometric, and mobile check-in included.",
        metaKeywords: "HR software India, payroll software, attendance management, employee lifecycle, Karya, Scitus",
        name: "Karya",
        sub: "HR & people operations",
        icon: Users,
        tagline: "Attendance, payroll, lifecycle — one platform, one source of truth.",
        description:
            "Karya runs the people side of your business end-to-end — geo-aware attendance, statutory-compliant payroll, leave management, onboarding, and performance — without spreadsheet chaos and without three different vendors. It scales from a 30-person team to a 5,000-person multi-entity workforce on the same backbone.",
        pillars: [
            {
                icon: Calendar,
                h: "Attendance & shifts, your way",
                p: "Geo-fence, face-match, biometric, kiosk, or mobile self-mark — mix and match by site and role.",
            },
            {
                icon: Banknote,
                h: "Payroll without panic",
                p: "Statutory packs for the geographies you operate in, salary structures, and one-click bank disbursement.",
            },
            {
                icon: UserPlus,
                h: "From offer to exit",
                p: "Onboarding, document kits, performance, learning, and full-and-final — one continuous lifecycle.",
            },
        ],
        features: {
            "Daily HR rhythm": [
                "Geo-fenced attendance with face-match and liveness detection",
                "Shift rosters with auto-balancing, swaps, and overtime caps",
                "Leave types, accrual rules, encashment, and compensatory off",
                "Real-time team presence map for managers and HRBP",
                "Regularisation requests with multi-level approval flows",
            ],
            "Payroll without panic": [
                "Pay-cycle automation with full audit trail and what-if simulation",
                "Statutory deductions and filings (PF, ESI, PT, TDS, GST-2A, country packs)",
                "Salary structure templates with CTC reverse-calculation",
                "One-click bank disbursement (NEFT / RTGS / NACH / SEPA / ACH)",
                "Reimbursement and flex-benefit modules with policy-based limits",
            ],
            "People you keep": [
                "Onboarding flows: checklist, documents, e-sign (via Attestra), kit dispatch",
                "Performance reviews, OKRs, 360°, 1:1 notes, and calibration",
                "Skill mapping, learning paths, and certification tracking",
                "Employee self-service portal with payslips, tax declarations, and policy library",
                "Full-and-final and clearance with auto-recovery and exit interview",
            ],
        },
        howItWorks: [
            { h: "Set policy", p: "Configure leaves, shifts, pay components, statutory rules, and approval matrices once." },
            { h: "Operate daily", p: "Capture attendance, run payroll, approve requests — from web, mobile, or kiosk." },
            { h: "Grow people", p: "Layer in reviews, learning, and career frameworks as the org matures." },
        ],
        useCases: [
            { ind: "Retail", h: "Store roster sanity", p: "Shift swaps, geo-aware attendance, and store-level wage tracking across multiple formats and franchisee models." },
            { ind: "Healthcare", h: "Provider compliance", p: "Credential expiry tracking, OT/OPD rosters, and statutory filings — without month-end reconciliation pain." },
            { ind: "Services", h: "Utilisation & growth", p: "Billable hours per consultant, project assignments, and career frameworks linked to performance review cycles." },
        ],
        faqs: [
            { q: "Does Karya handle multi-country payroll?", a: "Yes. Statutory packs are available for India, UAE, Singapore, UK, US, and we ship new packs each major release. Custom local logic is supported via a rules engine." },
            { q: "Can I keep my existing payroll vendor?", a: "Yes. Karya can run attendance, leaves, and HR only — exporting clean payroll inputs to your existing provider in their required format." },
            { q: "How do you handle data privacy?", a: "Personal data is encrypted at rest (AES-256) and in transit (TLS 1.3). Access is role-based with audit logging. GDPR / DPDP / CCPA-aligned controls are available on Growth and Enterprise tiers." },
            { q: "Does it integrate with biometric devices?", a: "Yes — standard ESSL, ZKTeco, Matrix, and Suprema devices are supported out of the box. Custom devices via our edge agent." },
        ],
        tier: "Starts on Starter",
        industries: ["Retail", "Real Estate", "Healthcare", "Manufacturing", "Services"],
        launchUrl: "https://karya.scitus.ai/login",
        launchLabel: "Launch Karya",
        logo: { src: "/products/karya-logo.png", fit: "lockup", needsLightTile: true },
        heroShot: "/products/shots/karya-1.png",
        gallery: ["/products/shots/karya-2.png", "/products/shots/karya-3.png", "/products/shots/karya-4.png", "/products/shots/karya-5.png", "/products/shots/karya-6.png"],
    },
    {
        slug: "vyaahar",
        metaDescription: "Vyaahar is GST-ready billing and finance for growing businesses. Invoicing, AR, AP, and bank reconciliation in one stack.",
        metaKeywords: "GST billing software, accounting software India, invoicing, AR AP management, Vyaahar, Scitus",
        name: "Vyaahar",
        sub: "Finance & accounting backbone",
        icon: Receipt,
        tagline: "Invoicing, AR, AP, banking, and audit-ready books — in one stack.",
        description:
            "Vyaahar is the finance engine for multi-entity, multi-geography operations. It runs tax-compliant invoicing, AR/AP, bank reconciliation, and audit-ready financial reporting on one ledger — so you can stop stitching together QuickBooks, three spreadsheets, and your accountant's WhatsApp messages.",
        pillars: [
            {
                icon: ClipboardCheck,
                h: "Tax-compliant invoicing",
                p: "GST, VAT, sales-tax, and country-specific e-invoicing built in — switchable per entity per geography.",
            },
            {
                icon: Landmark,
                h: "Banking & reconciliation",
                p: "Multi-bank API import, AI-assisted auto-match, and clean exception workflows.",
            },
            {
                icon: Calculator,
                h: "Reports that satisfy auditors",
                p: "P&L, balance sheet, cashflow, ageing, and trial balance — drillable to source transaction in one click.",
            },
        ],
        features: {
            "Invoicing & AR": [
                "Tax-compliant invoice templates with QR / IRN / e-invoice payload",
                "Recurring invoices, subscription billing, and milestone-based receipts",
                "Customer ledger, statement runs, and dispute notes",
                "Collection reminders sequenced over email, WhatsApp, and SMS",
                "Credit limits and customer-level credit hold rules",
            ],
            "AP & cash control": [
                "Vendor bills with multi-level approval, GST reverse-charge, and TDS handling",
                "Bank API import with AI auto-match (≥95% on the first pass on most accounts)",
                "13-week rolling cashflow forecast with scenario stress tests",
                "Expense claims with receipt OCR and policy enforcement",
                "Inter-company transactions with auto-elimination at consolidation",
            ],
            "Audit-ready financials": [
                "P&L, balance sheet, ageing, and trial balance with prior-period overlay",
                "Drill-down from any line on any report all the way to the source voucher",
                "Cost-centre, project, location, and entity-level reporting on the same ledger",
                "External auditor access with read-only roles and full audit trail",
                "Period-lock with maker-checker controls and reopening workflows",
            ],
        },
        howItWorks: [
            { h: "Configure", p: "Chart of accounts, tax rules, entities, cost centres, and approval matrices — once." },
            { h: "Transact", p: "Issue invoices, capture bills, reconcile bank, manage expenses — daily, on web or mobile." },
            { h: "Close", p: "Period-end with reports clean and exceptions cleared in hours, not days." },
        ],
        useCases: [
            { ind: "Real Estate", h: "Demand letters & milestones", p: "Linked-to-project invoicing, milestone-based receipts, and live cashflow forecasts per asset and entity." },
            { ind: "Manufacturing", h: "Cost-of-goods sanity", p: "BOM-linked costing, production variance, landed-cost imports, and clean GST-ready output." },
            { ind: "Services", h: "Project profitability", p: "Time-based billing, recurring invoicing, and per-project P&L without manual export to spreadsheets." },
        ],
        faqs: [
            { q: "Will Vyaahar replace my accountant?", a: "No — it amplifies them. Your accountant gets clean books and audit-ready reports, while spending less time on data entry and reconciliation." },
            { q: "Does it support multiple currencies?", a: "Yes. Configure your base currency, transact in any number of others. Revaluation runs automatically at period end with full audit trail." },
            { q: "Can my CA log in directly?", a: "Yes. We provide read-only auditor roles with full drill-down, audit trail, and downloadable schedules in the formats your CA expects." },
            { q: "Does it integrate with the tax authority?", a: "Yes. India GST returns (GSTR-1, 3B, 9), e-invoice (IRN), and e-way bill are native. Other jurisdictions are added per the regional roadmap." },
            { q: "How does it consolidate across entities?", a: "Native multi-entity ledger with inter-company auto-elimination, foreign-currency translation, and consolidation rules — no parallel spreadsheet at month end." },
        ],
        tier: "Starts on Starter",
        industries: ["Retail", "Real Estate", "Manufacturing", "Services"],
        launchUrl: "https://vyaahar.com/login",
        launchLabel: "Open Vyaahar",
        logo: { src: "/products/vyaahar-logo.png", fit: "lockup" },
        heroShot: "/products/shots/vyaahar-1.png",
        gallery: ["/products/shots/vyaahar-2.png", "/products/shots/vyaahar-3.png", "/products/shots/vyaahar-4.png", "/products/shots/vyaahar-5.png"],
    },
    {
        slug: "attestra",
        metaDescription: "Attestra is audit-grade e-signing with tamper-evident trails, sequential and parallel workflows, and a full REST API.",
        metaKeywords: "e-signing India, digital signature, document signing, audit trail, Attestra, Scitus",
        name: "Attestra",
        sub: "Digital signing & approvals",
        icon: FileSignature,
        tagline: "From drafted contract to live in hours — with an audit trail your counsel will love.",
        description:
            "Attestra is your contracts, approvals, and signature layer. Drag fields onto any PDF, send to as many signers as the deal needs, capture identity proofs where the law requires, and store the signed document with a tamper-evident audit trail you can hand to any auditor or court.",
        pillars: [
            {
                icon: BadgeCheck,
                h: "Legally compliant signatures",
                p: "Aadhaar e-sign, DSC, OTP, click-wrap, and qualified signatures — recognised across major jurisdictions.",
            },
            {
                icon: Shield,
                h: "Tamper-evident audit trail",
                p: "Every signed document is hashed, timestamped, and verifiable by any third party — including the signers themselves.",
            },
            {
                icon: GitBranch,
                h: "Workflows, not just signatures",
                p: "Multi-party, sequential, parallel, and conditional approval flows — with comments, redlines, and version history.",
            },
        ],
        features: {
            "Send and sign": [
                "Drag-and-drop fields onto any PDF — text, date, checkbox, initial, signature",
                "Reusable templates with clause libraries and conditional fields",
                "Multi-party flows with sequencing, parallel branches, and skip-on-condition rules",
                "Bulk send for HR letters, vendor agreements, NDA campaigns, and policy roll-outs",
                "In-person signing kiosk mode for offline signing scenarios",
            ],
            "Trust and verification": [
                "Tamper-evident audit trail with cryptographic hash on every signed document",
                "Optional KYC step before signing (Aadhaar, passport, drivers licence, video)",
                "Identity proofs (email, SMS OTP, ID document upload, biometric capture)",
                "Verifiable certificate downloadable by any party — including non-customers",
                "Long-term archival with retention rules per document type",
            ],
            "Integrations": [
                "Native to Vyaahar (invoice and PO approvals)",
                "Native to Karya (onboarding kits, appraisal letters, F&F)",
                "Native to Vexen and Acrium (project sign-offs, vendor agreements)",
                "REST API and webhooks for everything else (Stripe, Slack, custom)",
                "Salesforce, HubSpot, and Microsoft 365 connectors for sales contracts",
            ],
        },
        howItWorks: [
            { h: "Template", p: "Upload your contract, NDA, or HR letter once; tag fields and reuse forever." },
            { h: "Send", p: "Add signers, sequencing, identity requirements, and conditional logic — hit send." },
            { h: "Sign & store", p: "Counterparties sign on any device; you receive the signed PDF + tamper-evident certificate." },
        ],
        useCases: [
            { ind: "Real Estate", h: "Buyer agreements", p: "Demand letters, agreement to sale, and possession handover signed digitally in days — not weeks of courier and paper." },
            { ind: "Services", h: "Vendor & client contracts", p: "MSAs, SoWs, NDAs — templated and sent in two clicks; signed before your coffee cools." },
            { ind: "Healthcare", h: "Consent & credentials", p: "Patient consents, provider credentials, partner agreements — all on one auditable trail with retention policies built in." },
        ],
        faqs: [
            { q: "Are signatures legally enforceable?", a: "Yes. Attestra supports the full spectrum — from click-wrap acceptance to Aadhaar e-sign, DSC, and qualified signatures via integrated trust service providers. Every signature carries the audit trail required for enforceability under the IT Act, eIDAS, ESIGN, and UETA." },
            { q: "Can I sign on mobile?", a: "Yes. The signer experience is fully mobile-responsive — no app install required. iOS and Android native experiences available for high-volume signing teams." },
            { q: "How long are signed documents stored?", a: "Indefinitely on Growth and Enterprise tiers with per-document retention rules. Starter retains for 7 years with export available any time. WORM storage available for regulated industries." },
            { q: "What if a signer disputes a signature?", a: "The tamper-evident certificate includes IP, device, location, identity proofs, and document hash at every step. Dispute resolution is typically a single PDF download away." },
        ],
        tier: "All tiers",
        industries: ["All industries"],
        launchUrl: "https://attestrasign.com/",
        launchLabel: "Try Attestra",
        logo: { src: "/products/attestra-logo.png", fit: "lockup", needsLightTile: true },
        heroShot: "/products/shots/attestra-1.png",
        gallery: ["/products/shots/attestra-2.png", "/products/shots/attestra-3.png", "/products/shots/attestra-4.png", "/products/shots/attestra-5.png"],
    },
    {
        slug: "acrium",
        metaDescription: "Acrium controls project cost and procurement — from indent to GRN, committed to forecast, vendor scorecard to PO.",
        metaKeywords: "procurement software, project cost management, purchase order software, vendor management, Acrium, Scitus",
        name: "Acrium",
        sub: "Project cost & procurement control tower",
        icon: Briefcase,
        tagline: "Committed vs. budget, live. Procurement, on-rails. Vendors, scored.",
        description:
            "Acrium is the control tower for capital-intensive operations. It tracks committed cost against budget in real time, runs procurement with audit-grade approvals from indent to GRN, manages site inventory with the same discipline as a Tier-1 contractor, and grades vendors on the metrics that actually predict project outcomes.",
        pillars: [
            {
                icon: Wallet,
                h: "Live project cost truth",
                p: "Committed vs. budget vs. forecast on every cost head — refreshed the moment a PO or GRN is approved.",
            },
            {
                icon: PackageCheck,
                h: "Procurement on rails",
                p: "Indent → RFQ → comparative → PO → GRN — one disciplined workflow with no skipped steps and no exceptions in inbox.",
            },
            {
                icon: ClipboardList,
                h: "Vendor scorecards",
                p: "Vendor master with on-time, quality, price-vs-benchmark, dispute, and payment-cycle scores per category and project.",
            },
        ],
        features: {
            "Cost control": [
                "Budget tree with multi-level cost heads, packages, and WBS",
                "Live committed vs. actual vs. forecast on every cost head — drill to the source PO",
                "Variance alerts when commitments cross configurable thresholds (% or absolute)",
                "Change-order log with approval trail tied directly to budget impact",
                "Cashflow projection by project, by package, by month — built from approved commitments",
            ],
            "Procurement workflow": [
                "Indent → RFQ → comparative → PO → GRN, with maker-checker on every transition",
                "Multi-level approval matrix configurable by amount, cost head, entity, and project",
                "Price benchmarking: this vendor vs. peer vendors vs. last 12 months vs. similar projects",
                "Boardroom view: open POs, pending GRNs, payment-cycle health, and stuck approvals",
                "Reverse auctions and sealed-bid RFQs for high-value packages",
            ],
            "Inventory & vendors": [
                "Site store ledger with issues, returns, scrap, and physical reconciliation",
                "Material requirement planning tied directly to schedule milestones",
                "Vendor scorecards on on-time, quality, dispute, and payment cycle — per category and project",
                "Vendor onboarding with KYC, document collection, and digital signing (via Attestra)",
                "Vendor 360°: spend, performance, payment history, contracts, and pending dues in one view",
            ],
        },
        howItWorks: [
            { h: "Model", p: "Configure projects, budgets, cost heads, approval matrices, and vendor master." },
            { h: "Operate", p: "Site, procurement, and accounts work the same disciplined workflow from a single source of truth." },
            { h: "Steer", p: "Leadership runs cost and procurement from a single boardroom dashboard — refreshed live." },
        ],
        useCases: [
            { ind: "Real Estate", h: "Tower-by-tower cost truth", p: "Committed vs. budget per tower, per package, per vendor — board-pack ready every Monday morning." },
            { ind: "Manufacturing", h: "Capex & MRO discipline", p: "Capex projects, plant MRO purchases, and store-level inventory under one approval matrix and one vendor master." },
            { ind: "Services", h: "Project-cost governance", p: "Multi-project commercial teams run a single, audited procurement and cost workflow — no per-project spreadsheet wars." },
        ],
        faqs: [
            { q: "How does Acrium fit alongside our existing ERP?", a: "Acrium sits as the operating layer for project cost and procurement. It posts approved transactions to your ERP via API — no parallel ledger, no end-of-month reconciliation pain." },
            { q: "Can we run multiple entities and projects?", a: "Yes. Acrium is multi-entity and multi-project by design, with entity-level approval matrices, cost rules, and vendor masters. Inter-entity transactions are handled natively." },
            { q: "Do site teams need to be online to use it?", a: "The mobile capture for GRNs, store issues, and physical reconciliation works fully offline and syncs on reconnect. Approval workflows queue cleanly even when sites have intermittent connectivity." },
            { q: "Is there an audit trail?", a: "Every transaction — indent, RFQ, comparative, PO, GRN, change order — carries an immutable, signed trail of who acted, when, and on what. The audit pack exports as a single PDF per project." },
            { q: "How is Acrium different from Vexen?", a: "Vexen owns operational execution — schedule, milestones, OEE, SLA, TAT. Acrium owns the commercial layer — project cost, procurement, inventory, vendor control. They share one customer/project master and most operators run both together." },
        ],
        tier: "Growth and above",
        industries: ["Real Estate", "Manufacturing", "Services", "Infrastructure"],
        demo: {
            available: true,
            personas: ["Chairman", "Engineering Head", "Procurement", "Site Engineer", "Store Manager"],
            note: "Acrium ships with a fully interactive live demo covering five role-based personas — boardroom, engineering, procurement, site, and store. Request access and we'll provision a sandboxed environment in your name within one business day.",
        },
        launchUrl: "https://acrium.scitus.ai/login",
        launchLabel: "Enter Acrium",
        logo: { src: "/products/acrium-logo.svg", fit: "mark" },
        heroShot: "/products/shots/acrium-1.png",
        gallery: ["/products/shots/acrium-2.png", "/products/shots/acrium-3.png", "/products/shots/acrium-4.png", "/products/shots/acrium-5.png"],
    },
    {
        slug: "kretara",
        metaDescription: "Kretara brings procurement intelligence to retail teams — multi-level approvals, price benchmarking, and an AI copilot.",
        metaKeywords: "procurement intelligence, retail procurement, vendor scorecard, approval workflow, Kretara, Scitus",
        name: "Kretara",
        sub: "Procurement intelligence",
        icon: ShoppingCart,
        tagline: "Procurement, refined. Decisions, intelligent.",
        description:
            "Kretara is a premium procurement workspace for multi-store, multi-category buying teams. Multi-level approvals enforced without inbox chaos, vendor performance scored against the metrics that move margin, inventory truth refreshed from the shop floor — and an AI-ready data layer that turns every PO, GRN, and price negotiation into a feature your category managers can actually use.",
        pillars: [
            {
                icon: GitBranch,
                h: "Multi-step approvals",
                p: "Indent → review → approval → release — a configurable approval matrix by amount, category, store, and entity. No skipped steps, no email forwards, full audit trail.",
            },
            {
                icon: Shield,
                h: "Role-based access",
                p: "Admin, Manager, Officer, Viewer — and any custom role you need. Every screen, every action, every data slice gated by role and store hierarchy.",
            },
            {
                icon: LineChart,
                h: "Live analytics",
                p: "Spend by category, by vendor, by store, by month — refreshed the moment a PO is approved. Variance, price-vs-benchmark, and cycle-time signals that surface before they hit margin.",
            },
            {
                icon: Sparkles,
                h: "AI-ready data",
                p: "Every transaction lands in a clean, structured layer designed for retrieval — so the copilot can answer category and vendor questions in plain English, grounded only in your own data.",
            },
        ],
        features: {
            "Approval discipline": [
                "Configurable approval matrix by amount, category, entity, and store",
                "Maker-checker on every transition (indent, RFQ, PO, GRN, payment release)",
                "Parallel and sequential approval flows with auto-escalation on SLA breach",
                "Mobile-first approvals — approve / query / reject from the road with full context",
                "Immutable audit log of who approved what, when, and on what basis",
            ],
            "Vendor & category intelligence": [
                "Vendor master with KYC, document collection, and onboarding via Attestra",
                "Vendor scorecards — on-time, quality, dispute, price-vs-benchmark, payment-cycle",
                "Category spend trees with year-on-year and store-on-store deltas",
                "Price benchmarking: this vendor vs. peer vendors vs. last 12 months",
                "Auto-flag anomalies — duplicate POs, off-contract spend, sudden price drift",
            ],
            "Workspace & data": [
                "Role-based access at screen, action, and data-row granularity",
                "Single sign-on (SAML, Google Workspace, M365) on every plan",
                "Open API + webhooks for ERP, POS, and accounting sync",
                "AI copilot grounded in your procurement history — plain-English category queries",
                "Live dashboards exportable as branded PDFs for monthly business reviews",
            ],
        },
        howItWorks: [
            { h: "Model", p: "Configure categories, vendors, approval matrices, store hierarchy, and role policies." },
            { h: "Operate", p: "Category and store buying teams run the same disciplined workflow — indent to GRN — from desktop or mobile." },
            { h: "Decide", p: "Heads of procurement and category run the business from a live dashboard, with the AI copilot one prompt away." },
        ],
        useCases: [
            { ind: "Retail", h: "Category buying intelligence", p: "Live spend, vendor performance, and price benchmarks across every category and store format — no more spreadsheet-of-spreadsheets at month end." },
            { ind: "Services", h: "Multi-entity procurement discipline", p: "Shared services teams run one approval matrix and one vendor master across every operating entity — with clean inter-entity allocation." },
            { ind: "Manufacturing", h: "Indirect spend control", p: "MRO and indirect categories run with the same approval rigour as direct production buying — finally visible, finally controlled." },
        ],
        faqs: [
            { q: "How is Kretara different from Acrium?", a: "Acrium is built for capital-project businesses — real estate, EPC, infrastructure — where the unit is a project, a budget, and a WBS. Kretara is built for category-driven, multi-store buying — retail, services, manufacturing indirect spend — where the unit is a category, a vendor, and a price benchmark. Most customers use one or the other; some run both." },
            { q: "Can it sit on top of our existing ERP?", a: "Yes. Kretara posts approved transactions to your ERP via API. There's no parallel ledger and no end-of-month reconciliation pain — your ERP remains the system of record for finance." },
            { q: "How granular is the role-based access?", a: "Every screen, every action, and every data row can be gated by role and store hierarchy. A regional manager sees only their stores. A category officer sees only their categories. The audit log records every override, by whom, and why." },
            { q: "What does 'AI-ready data' mean in practice?", a: "Every transaction — indent, RFQ comparison, PO, GRN, payment — lands in a clean, structured layer designed for retrieval. The copilot can answer questions like 'which vendor has the worst on-time score in dairy this quarter' in seconds, grounded only in your own data." },
            { q: "How fast is onboarding?", a: "Most teams are running approvals on real POs within two weeks. We migrate your vendor master, configure the approval matrix to your existing delegation of authority, and train champions across categories — then go live with parallel running for the first cycle." },
        ],
        tier: "Growth and above",
        industries: ["Retail", "Services", "Manufacturing", "Healthcare"],
        demo: {
            available: true,
            personas: ["Admin", "Manager", "Officer", "Viewer"],
            note: "Kretara ships with four out-of-the-box role personas — Admin, Manager, Officer, Viewer — each with its own approval scope and dashboard. Request access and we'll provision a sandboxed workspace in your name within one business day.",
        },
        launchUrl: "https://kretara.scitus.ai",
        launchLabel: "Enter Kretara",
        logo: { src: "/products/kretara-logo.svg", fit: "lockup", compactSrc: "/products/kretara-mark.svg" },
        heroShot: "/products/shots/kretara-1.png",
        gallery: [
            "/products/shots/kretara-2.png",
            "/products/shots/kretara-3.png",
            "/products/shots/kretara-4.png",
            "/products/shots/kretara-5.png",
        ],
    },
];

export const getProduct = (slug) => PRODUCTS.find((p) => p.slug === slug);
